import { Article, WithContext } from 'schema-dts'
import { Schemas } from '~/api/gen'
import { MetaData } from '~/components/Meta'
import { BreadcrumbData } from '~/features/shared/breadcrumbList/types'
import { getMetaTitle } from '~/features/shared/coordinate/lib/getMetaTitle'
import {
  getBrandPart,
  getCategoryPart,
  getMemberName,
} from '~/features/shared/meta/pageData'
import { buildItemListUrl } from '~/features/shared/search/item/buildItemListUrl'
import { dateFormatToMD } from '~/lib/dateUtil'
import { sexToGender } from '~/lib/gender/converter'
import { getLdBrand, getLdOffer, getLdGender } from '~/lib/jsonLd'

export const getMetaDescription = (
  member: Schemas.member_small_with_shop_brands,
  item: Schemas.item_resource_connect | null
) => {
  const memberName = getMemberName(member)
  const itemName = item?.name ? `「${item.name}」` : ''
  const brandName = getBrandPart(item?.brand.name)
  const categoryName = getCategoryPart(
    item?.item_category?.name,
    item?.item_child_category?.name
  )
  return `${brandName}${categoryName}${itemName}を使った${memberName}のコーディネートです。WEARはモデル・俳優・ショップスタッフなどの着こなしをチェックできるファッションコーディネートサイトです。`
}

const getArticleJsonLd = (
  title: string,
  coordinate: Schemas.coordinate_large_with_member_shop_brands,
  coordinateItems: Schemas.coordinate_item_resource[]
): WithContext<Article> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    author: {
      '@type': 'Person',
      name: coordinate.member.nick_name,
      url: `https://wear.jp/${coordinate.member.user_name}/`,
      affiliation: coordinate.member.shop
        ? {
            '@type': 'Organization',
            name: coordinate.member.shop.name,
            url: `https://wear.jp/shop/${coordinate.member.shop.id}/`,
            image: coordinate.member.shop.image.url ?? undefined,
            address: {
              '@type': 'PostalAddress',
              streetAddress: coordinate.member.shop.address,
              addressCountry: 'JP',
            },
          }
        : undefined,
      gender: getLdGender(sexToGender(coordinate.member.sex?.id)),
      height: coordinate.member.height
        ? {
            '@type': 'QuantitativeValue',
            value: coordinate.member.height,
            unitCode: 'CMT',
          }
        : undefined,
    },
    datePublished: coordinate.published_at ?? undefined,
    dateModified: coordinate.updated_at,
    articleBody: coordinate.content,
    url: `https://wear.jp/${coordinate.member.user_name}/${coordinate.id}/`,
    image: [
      coordinate.image.url,
      ...coordinate.sub_images.map((image) => image.url),
    ],
    mentions: coordinateItems
      .map((item) => {
        // 着用アイテムはインデックスさせていないのでスキップ
        if (item.item.id === null) return
        return {
          '@type': 'Product' as const,
          image: item.image.url,
          url: `https://wear.jp/item/${item.item.id}/`,
          name: item.name,
          brand:
            item.item.brand.name && item.item.brand.url
              ? getLdBrand(item.item.brand.name, item.item.brand.url)
              : undefined,
          category: `https://wear.jp${buildItemListUrl({
            category: {
              key: item.item.item_category?.keyword,
              id: item.item.item_category?.id,
              child: item.item.item_child_category?.keyword
                ? {
                    key: item.item.item_child_category.keyword,
                    id: item.item.item_child_category.id,
                  }
                : undefined,
            },
          })}`,
          color: item.color_group?.name,
          size: item.size ?? undefined,
          offers: getLdOffer(
            item.item.price,
            item.item.purchase_items.length > 0
          ),
        }
      })
      .filter((item) => item !== undefined),
  }
}

export const getMetaData = (
  coordinate: Schemas.coordinate_large_with_member_shop_brands,
  coordinateItems: Schemas.coordinate_item_resource[]
): MetaData => {
  const metaItem = coordinateItems[0] ? coordinateItems[0].item : null
  const title = getMetaTitle(
    coordinate.member,
    metaItem?.item_category?.name,
    metaItem?.item_child_category?.name,
    metaItem?.brand.name
  )
  const articleJsonLd = getArticleJsonLd(title, coordinate, coordinateItems)
  return {
    title,
    description: getMetaDescription(coordinate.member, metaItem),
    url: `https://wear.jp/${coordinate.member.user_name}/${coordinate.id}/`,
    appUrl: `wear2020://wear.jp/coordinate/${coordinate.id}/`,
    robots: true,
    ogImageUrl: coordinate.image.url_500,
    ogType: 'article',
    jsonLdList: [articleJsonLd],
  }
}

export const getBreadcrumb = (
  coordinate: Schemas.coordinate_large_with_member_shop_brands
): BreadcrumbData[] => {
  const publishedDate = coordinate.published_at
    ? dateFormatToMD(coordinate.published_at)
    : ''
  return [
    coordinate.member.shop
      ? {
          name: coordinate.member.shop.name,
          path: `/shop/${coordinate.member.shop.id}/`,
        }
      : [],
    {
      name: coordinate.member.nick_name,
      path: `/${coordinate.member.user_name}/`,
    },
    {
      name: `${publishedDate} コーディネート`,
    },
  ].flat()
}
